import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import { KV, KVRow } from '../components/KV';
import UserAppSection from '../components/UserAppSection';
import AddAppPanel from '../components/AddAppPanel';
import type { AppPayload } from '../renderers';

interface User {
  user_id: string;
  tenant_id: string;
  first_name: string;
  last_name: string;
  email: string;
  email_domain: string;
  role: string;
  status: string;
  verified_phones?: string[];
  created_at: string | null;
  updated_at: string | null;
}

function format_dt(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

export default function UserDetail() {
  const { tenant_id = '', user_id = '' } = useParams<{
    tenant_id: string;
    user_id: string;
  }>();
  const qc = useQueryClient();

  const userQuery = useQuery({
    queryKey: ['user', user_id],
    queryFn: () =>
      api.get<User>(`/api/tenants/${tenant_id}/users/${user_id}`),
  });
  const appsQuery = useQuery({
    queryKey: ['user', user_id, 'apps'],
    queryFn: () =>
      api.get<{ apps: AppPayload[] }>(
        `/api/tenants/${tenant_id}/users/${user_id}/apps`
      ),
  });

  function set_apps_changed() {
    qc.invalidateQueries({ queryKey: ['user', user_id, 'apps'] });
  }

  const [editing, set_editing] = useState(false);
  const [draft_first, set_draft_first] = useState('');
  const [draft_last, set_draft_last] = useState('');
  const [save_busy, set_save_busy] = useState(false);
  const [save_error, set_save_error] = useState<string | null>(null);

  function start_edit() {
    set_draft_first(userQuery.data?.first_name ?? '');
    set_draft_last(userQuery.data?.last_name ?? '');
    set_save_error(null);
    set_editing(true);
  }

  const [status_busy, set_status_busy] = useState(false);
  const [status_error, set_status_error] = useState<string | null>(null);
  const [role_busy, set_role_busy] = useState(false);
  const [role_error, set_role_error] = useState<string | null>(null);

  async function change_role(next: string) {
    if (next === userQuery.data?.role) return;
    set_role_busy(true);
    set_role_error(null);
    try {
      await api.patch(`/api/tenants/${tenant_id}/users/${user_id}/role`, { role: next });
      qc.invalidateQueries({ queryKey: ['user', user_id] });
      qc.invalidateQueries({ queryKey: ['tenant', tenant_id, 'users'] });
    } catch (err) {
      set_role_error(err instanceof ApiError ? err.reason : String(err));
    } finally {
      set_role_busy(false);
    }
  }

  async function toggle_status() {
    const next = userQuery.data?.status === 'active' ? 'deactivated' : 'active';
    set_status_busy(true);
    set_status_error(null);
    try {
      await api.patch(`/api/tenants/${tenant_id}/users/${user_id}/status`, {
        status: next,
      });
      qc.invalidateQueries({ queryKey: ['user', user_id] });
      qc.invalidateQueries({ queryKey: ['tenant', tenant_id, 'users'] });
    } catch (err) {
      set_status_error(err instanceof ApiError ? err.reason : String(err));
    } finally {
      set_status_busy(false);
    }
  }

  async function save_names(e: FormEvent) {
    e.preventDefault();
    set_save_busy(true);
    set_save_error(null);
    try {
      await api.patch(`/api/tenants/${tenant_id}/users/${user_id}`, {
        first_name: draft_first.trim(),
        last_name: draft_last.trim(),
      });
      qc.invalidateQueries({ queryKey: ['user', user_id] });
      qc.invalidateQueries({ queryKey: ['tenant', tenant_id, 'users'] });
      set_editing(false);
    } catch (err) {
      set_save_error(err instanceof ApiError ? err.reason : String(err));
    } finally {
      set_save_busy(false);
    }
  }

  return (
    <div className="container">
      <p style={{ marginTop: 0 }}>
        <Link to={`/tenants/${tenant_id}`} className="muted">
          ← Tenant
        </Link>
      </p>

      {userQuery.isLoading && <p>Loading user...</p>}
      {userQuery.error && (
        <p className="error">
          Failed to load user:{' '}
          {userQuery.error instanceof ApiError
            ? userQuery.error.reason
            : String(userQuery.error)}
        </p>
      )}
      {userQuery.data && (
        <>
          {editing ? (
            <form
              onSubmit={save_names}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr auto auto',
                gap: 8,
                alignItems: 'center',
                marginBottom: 16,
              }}
            >
              <input
                value={draft_first}
                onChange={(e) => set_draft_first(e.target.value)}
                required
                maxLength={80}
                placeholder="First name"
                autoFocus
                style={{ fontSize: 22, fontWeight: 600, padding: '6px 10px' }}
              />
              <input
                value={draft_last}
                onChange={(e) => set_draft_last(e.target.value)}
                required
                maxLength={80}
                placeholder="Last name"
                style={{ fontSize: 22, fontWeight: 600, padding: '6px 10px' }}
              />
              <button type="submit" disabled={save_busy}>
                {save_busy ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                className="secondary"
                onClick={() => set_editing(false)}
                disabled={save_busy}
              >
                Cancel
              </button>
            </form>
          ) : (
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 4 }}>
              <h1 style={{ margin: 0 }}>
                {userQuery.data.first_name} {userQuery.data.last_name}
              </h1>
              <button
                className="secondary"
                onClick={start_edit}
                style={{ padding: '4px 10px', fontSize: 12 }}
              >
                Edit name
              </button>
            </div>
          )}
          {save_error && (
            <div className="error" style={{ marginBottom: 16 }}>
              Failed to save: {save_error}
            </div>
          )}
          <p className="muted" style={{ marginTop: 0, marginBottom: 24 }}>
            {userQuery.data.email}
          </p>
          <div className="card" style={{ marginBottom: 24 }}>
            <KV>
              <KVRow
                label="User ID"
                value={<code>{userQuery.data.user_id}</code>}
              />
              <KVRow
                label="Tenant ID"
                value={
                  <Link to={`/tenants/${userQuery.data.tenant_id}`}>
                    <code>{userQuery.data.tenant_id}</code>
                  </Link>
                }
              />
              <KVRow label="Email domain" value={userQuery.data.email_domain} />
              <KVRow
                label="Verified phones"
                value={
                  (userQuery.data.verified_phones?.length ?? 0) === 0 ? (
                    <span className="muted">— (none)</span>
                  ) : (
                    <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {(userQuery.data.verified_phones ?? []).map((p) => (
                        <code key={p}>{p}</code>
                      ))}
                    </span>
                  )
                }
              />
              <KVRow
                label="Role"
                value={
                  <span style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <select
                      value={userQuery.data.role}
                      onChange={(e) => change_role(e.target.value)}
                      disabled={role_busy}
                      style={{ padding: '4px 8px', fontSize: 13 }}
                    >
                      <option value="owner">owner</option>
                      <option value="admin">admin</option>
                      <option value="member">member</option>
                    </select>
                    {role_busy && <span className="muted" style={{ fontSize: 12 }}>Saving…</span>}
                    {role_error && (
                      <span className="error" style={{ fontSize: 12 }}>
                        {role_error}
                      </span>
                    )}
                  </span>
                }
              />
              <KVRow
                label="Status"
                value={
                  <span style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <span>{userQuery.data.status}</span>
                    <button
                      className="secondary"
                      onClick={toggle_status}
                      disabled={status_busy}
                      style={{ padding: '4px 10px', fontSize: 12 }}
                    >
                      {status_busy
                        ? 'Saving...'
                        : userQuery.data.status === 'active'
                          ? 'Deactivate'
                          : 'Activate'}
                    </button>
                    {status_error && (
                      <span className="error" style={{ fontSize: 12 }}>
                        {status_error}
                      </span>
                    )}
                  </span>
                }
              />
              <KVRow
                label="Created"
                value={format_dt(userQuery.data.created_at)}
              />
              <KVRow
                label="Updated"
                value={format_dt(userQuery.data.updated_at)}
              />
            </KV>
          </div>
        </>
      )}

      <h2 style={{ marginTop: 8 }}>App data</h2>
      {appsQuery.isLoading && <p>Loading app data...</p>}
      {appsQuery.error && (
        <p className="error">
          Failed to load app data:{' '}
          {appsQuery.error instanceof ApiError
            ? appsQuery.error.reason
            : String(appsQuery.error)}
        </p>
      )}
      {appsQuery.data && (
        <>
          {appsQuery.data.apps.length === 0 ? (
            <div className="card" style={{ marginBottom: 16 }}>
              <p className="muted" style={{ margin: 0 }}>
                This user has no app data yet. Use "Add app" to provision one.
              </p>
            </div>
          ) : (
            appsQuery.data.apps.map((app) => (
              <UserAppSection
                key={app.app_name}
                tenant_id={tenant_id}
                user_id={user_id}
                app={app}
                on_changed={() => set_apps_changed()}
              />
            ))
          )}
          <AddAppPanel
            tenant_id={tenant_id}
            user_id={user_id}
            existing_apps={appsQuery.data.apps.map((a) => a.app_name)}
            on_added={() => set_apps_changed()}
          />
        </>
      )}
    </div>
  );
}
